import { hydrateElement } from "@modules/hydrate/hydrate";

const easeInOutCubic = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const getHeaderOffset = () => 0;

const getCurrentScroll = () => {
    if (window.lenis && Number.isFinite(window.lenis.scroll)) {
        return window.lenis.scroll;
    }

    return window.scrollY || 0;
};

const waitForLenis = async (timeoutMs = 2500) => {
    if (window.lenis) return window.lenis;

    const pending = window.__lenisInit;

    if (!pending) return null;

    let timer;

    try {
        return await Promise.race([
            pending,
            new Promise((_, reject) => {
                timer = window.setTimeout(() => reject(new Error("Lenis init timeout")), timeoutMs);
            }),
        ]);
    } catch {
        return null;
    } finally {
        if (timer) window.clearTimeout(timer);
    }
};

const resolveAnchorTarget = target => target?.closest?.(".snap") || target;

const resolveScrollY = target => {
    const node = resolveAnchorTarget(target);

    return Math.max(0, Math.round(node.getBoundingClientRect().top + getCurrentScroll() - getHeaderOffset()));
};

const computeDuration = (fromY, toY, { minDuration, maxDuration, speed }) => {
    const distance = Math.abs(toY - fromY);

    if (distance < 1) return 0;

    const pxPerSecond = speed ?? (window.innerHeight || 800) / maxDuration;

    return Math.min(maxDuration, Math.max(minDuration, distance / pxPerSecond));
};

const prepareAnchorTarget = async target => {
    const section = target?.closest?.("[data-module]") ?? target;

    if (!section?.dataset?.module || section.dataset.hydrated === "true") {
        return;
    }

    hydrateElement(section);

    await new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
};

/**
 * Scroll fluide vers les ancres internes.
 * Durée proportionnelle à la distance (vitesse visuelle constante).
 *
 * @param {Object}   [options]
 * @param {string}   [options.links='a[href]']
 * @param {number}   [options.duration=1.2]       Durée max (s) — ~1 viewport de scroll.
 * @param {number}   [options.minDuration=0.35]   Durée min (s) pour ancres proches.
 * @param {number}   [options.speed]              Vitesse (px/s). Défaut : innerHeight / duration.
 * @param {Function} [options.easing=easeInOutCubic]
 * @param {Function} [options.getOffset=getHeaderOffset]
 */
export default function smoothAnchors(options = {}) {
    const {
        links = "a[href]",
        duration: maxDuration = 1.2,
        minDuration = 0.35,
        speed,
        easing = easeInOutCubic,
        getOffset = getHeaderOffset,
    } = options;

    const scrollToTarget = target => {
        const fromY = getCurrentScroll();
        const scrollY = typeof target === "number"
            ? target
            : Math.max(0, Math.round(
                resolveAnchorTarget(target).getBoundingClientRect().top
                + fromY
                - getOffset(),
            ));

        const travelDuration = computeDuration(fromY, scrollY, {
            minDuration,
            maxDuration,
            speed,
        });

        if (window.lenis) {
            window.lenis.scrollTo(scrollY, {
                duration: travelDuration,
                easing,
                force: true,
                lock: false,
            });
            return;
        }

        window.scrollTo({ top: scrollY, behavior: "smooth" });
    };

    const getAnchorTarget = href => {
        const i = href.indexOf("#");
        if (i === -1) return null;
        const id = decodeURIComponent(href.slice(i + 1));
        return id ? document.getElementById(id) : null;
    };

    const onClick = async event => {
        const node = event.target;
        if (!node || typeof node.closest !== "function") return;

        const link = node.closest(links);
        if (!link || !document.contains(link)) return;

        const href = link.getAttribute("href") || "";

        if (href === "#" || href === "#top" || href === "/") {
            if (href === "/" && window.location.pathname !== "/") return;

            event.preventDefault();
            await waitForLenis();
            scrollToTarget(0);
            history.pushState(null, "", window.location.pathname + window.location.search);
            return;
        }

        const target = getAnchorTarget(href);
        if (!target) return;

        event.preventDefault();
        await waitForLenis();
        await prepareAnchorTarget(target);
        scrollToTarget(target);

        if (target.id) history.pushState(null, "", `#${target.id}`);
    };

    document.addEventListener("click", onClick);

    return () => document.removeEventListener("click", onClick);
}
